/**
 * Metadata Redux-Saga middleware.
 *
 * Listens for the `"UPDATE_METADATA"` action dispatched by
 * `metadata-handler.ts` after a successful `/metadata` API call and:
 * 1. Writes the full metadata payload to the Redux `metadata` slice.
 * 2. Restores the user's Market Watch from `marketWatchScrips` — the exact
 *    symbol list the panel showed last session — when one is stored.
 * 3. Otherwise resolves the user's default watchlist, selects it, and
 *    cross-references its scrip IDs with the feed instrument catalogue to
 *    populate the `watchlist` slice with the full scrip objects (symbol, ID,
 *    segment) — then persists that list, so it too survives the next login.
 */
import { call, put, takeLatest, select } from "redux-saga/effects";

import { metadataReducer } from "@store/metadata-slice";
import {
  addScripsReducer,
  addScripIdsReducer,
  updateSelectedWatchlistReducer,
  toScripId,
} from "@store/watchlist-slice";
import { createWatchlistInAmibrokerHandler } from "@service/watchlist-handler";
import {
  primeSyncedMarketWatch,
  syncMarketWatchScrips,
} from "@service/market-watch-handler";

/** Resolve the user's default watchlist into a dropdown option (undefined if it no longer exists). */
function resolveSelectedWatchlist(metadata: any) {
  const defaultWatchlistId = metadata?.userDefaultWatchlist?.watchlistId;
  if (!defaultWatchlistId) return undefined;

  let selectedWatchlist;
  (metadata.userWatchlist || []).forEach((watchlist) => {
    if (watchlist.id === defaultWatchlistId) {
      selectedWatchlist = {
        value: watchlist.id,
        label: watchlist.name,
        scrips: watchlist.scrips,
      };
    }
  });
  (metadata.defaultWatchlist || []).forEach((watchlist) => {
    if (watchlist.id === defaultWatchlistId) {
      selectedWatchlist = {
        value: watchlist.id,
        label: watchlist.name,
        scrips: watchlist.scrips,
      };
    }
  });
  return selectedWatchlist;
}

/**
 * Turn stored scrip ids back into panel rows, in the stored order.
 *
 * Ids the instrument catalogue no longer knows (delisted symbols) are skipped
 * rather than failing the restore.
 */
function materializeRows(scripIds: number[], feedsScrips: any[]) {
  const scripById = new Map<string, any>();
  (feedsScrips || []).forEach((feed) =>
    (feed?.scrips || []).forEach((scrip) =>
      scripById.set(String(scrip.id), { ...scrip, segment: feed.name })
    )
  );

  return scripIds
    .map((scripId) => scripById.get(String(scripId)))
    .filter((scrip) => !!scrip)
    .map((scrip) => ({
      symbol: scrip.symbol,
      id: toScripId(scrip.id),
      segment: scrip.segment,
    }));
}

export function* updateMetadataSaga({ payload: { metadata }, type }) {
  yield put(metadataReducer(metadata));

  // Push symbolScripId map and all watchlists to AmiBroker if enabled.
  // Done BEFORE the early return so it always runs regardless of default watchlist.
  try {
    const amiEnabled: boolean = yield select((state: any) => state.amibroker?.isEnabled || false);
    if (amiEnabled) {
      const symbolScripId: Record<string, string> =
        (metadata.feedsScrips || []).reduce((acc: Record<string, string>, feed: any) => {
          (feed?.scrips || []).forEach((scrip: any) => { acc[String(scrip.id)] = scrip.symbol; });
          return acc;
        }, {} as Record<string, string>);

      if (Object.keys(symbolScripId).length > 0) {
        const { pushSymbolScripIdMap } = require("../service/ipc-handler");
        pushSymbolScripIdMap(symbolScripId); // fire-and-forget
      }

      // Push all watchlists (feedsScrips + userWatchlist + defaultWatchlist)
      yield call(createWatchlistInAmibrokerHandler, {
        feedsScrips: metadata.feedsScrips || [],
        userWatchlist: metadata.userWatchlist || [],
        defaultWatchlist: metadata.defaultWatchlist || [],
      });
    }
  } catch {
    // Silent — AmiBroker sync is non-critical
  }

  // ── Restore the user's Market Watch ────────────────────────────
  // The server keeps the panel's exact symbol list so the next login resumes
  // where the user left off. When there is one it wins over the default-watchlist
  // template below, because it is what the panel actually showed last time.
  const persistedScripIds: number[] = (metadata.marketWatchScrips || [])
    .map(toScripId)
    .filter((scripId: number) => Number.isFinite(scripId));

  if (persistedScripIds.length > 0) {
    const restoredRows = materializeRows(persistedScripIds, metadata.feedsScrips);

    // Baseline is what the panel will show — any id the catalogue dropped stays
    // untouched on the server rather than being deleted as a phantom removal.
    primeSyncedMarketWatch(restoredRows.map((scrip) => scrip.id));
    yield put(addScripsReducer(restoredRows));
    yield put(addScripIdsReducer(restoredRows.map((scrip) => scrip.id)));

    // Keep the dropdown on the user's default watchlist; only the rows are ours.
    const restoredSelection = resolveSelectedWatchlist(metadata);
    if (restoredSelection) yield put(updateSelectedWatchlistReducer(restoredSelection));
    return;
  }

  if (!metadata?.userDefaultWatchlist?.watchlistId) {
    return;
  }

  const selectedWatchlist = resolveSelectedWatchlist(metadata);

  const scrips = (metadata.feedsScrips || [])
    .flatMap((feed) =>
      (feed?.scrips || []).map((scrip) => ({ ...scrip, segment: feed.name }))
    )
    .filter((scrip) => selectedWatchlist?.scrips?.includes(scrip.id))
    .map((scrip) => ({
      symbol: scrip.symbol,
      id: scrip.id,
      segment: scrip.segment,
    }));

  yield put(updateSelectedWatchlistReducer(selectedWatchlist));
  yield put(addScripsReducer(scrips));
  yield put(addScripIdsReducer(scrips.map((scrip) => scrip.id)));

  // First login: nothing is stored yet, so the panel came from the watchlist
  // template. Push it now — otherwise the next login would restore only the
  // handful of symbols the user happened to add by hand afterwards.
  primeSyncedMarketWatch([]);
  syncMarketWatchScrips(scrips.map((scrip) => scrip.id));
}

export function* metadataWatcher() {
  yield takeLatest("UPDATE_METADATA", updateMetadataSaga);
}
